import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { useUiStore } from '@/features/settings/ui-store.js';
import {
  deriveCan,
  resolveCurrentUserRole,
} from '@/features/workspace/workspace-access.js';
import {
  workspaceMembersQueryOptions,
  workspacesQueryOptions,
} from '@/lib/api/workspaces.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

/** What the Skills pane needs to know about the workspace the user is currently in. */
export interface WorkspaceSkillAccess {
  /** The selected workspace's id, or null when it can't hold skills (no selection, or a personal workspace). */
  workspaceId: string | null;
  /** Its display name — the Workspace tab's label, and the wording of the Share action. */
  workspaceName: string | null;
  /** True when the caller may author in it (owner or editor). Mirrors the server's authoring gate. */
  canManage: boolean;
}

/**
 * Resolves whether a workspace can hold skills and whether the caller may author them, the same way
 * the workspace manage page does (membership directory → role → `can` set). A personal workspace
 * resolves to `workspaceId: null`: it is already the private scope, so the pane hides the Workspace
 * tab there rather than offering a duplicate of Personal.
 *
 * `override` targets a specific workspace instead of the switcher's selection — see
 * `useSelectedWorkspaceSkillsQuery`, which must be given the same value or the two disagree.
 */
export function useWorkspaceSkillAccess(
  override?: string | null,
): WorkspaceSkillAccess {
  const selectedFromSwitcher = useUiStore((state) => state.selectedWorkspaceId);
  const selectedWorkspaceId =
    override === undefined ? selectedFromSwitcher : override;
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const { data: workspaces } = useQuery(workspacesQueryOptions());
  const workspace =
    workspaces?.find((candidate) => candidate.id === selectedWorkspaceId) ??
    null;
  const shared = workspace !== null && workspace.kind === 'shared';
  const { data: members } = useQuery({
    ...workspaceMembersQueryOptions(selectedWorkspaceId ?? ''),
    enabled: shared,
  });

  if (!shared || workspace === null) {
    return { workspaceId: null, workspaceName: null, canManage: false };
  }
  const role = resolveCurrentUserRole({
    workspace,
    members,
    currentUserId: identity.identity?.userId ?? null,
    isAdmin: (identity.identity?.roles ?? []).includes('admin'),
  });
  return {
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    canManage: deriveCan(role).manageSettings,
  };
}
